import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { useText } from "../contexts/TextsContext";
import { ArrowUpRight, Calendar, Search, ChevronLeft, ChevronRight } from "lucide-react";

export default function BlogList() {
  const [search, setSearch] = useSearchParams();
  const q = search.get("q") || "";
  const cat = search.get("category") || "";
  const page = parseInt(search.get("page") || "1");
  const t = useText();

  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat) params.set("category", cat);
    params.set("page", page);
    params.set("per_page", "9");
    api.get(`/blog-search?${params}`).then(r => setData(r.data)).finally(() => setLoading(false));
    api.get("/blog-categories").then(r => setCategories(r.data));
  }, [q, cat, page]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(search);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== "page") next.delete("page");
    setSearch(next);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--solar)] mb-4">{t("blog.kicker")}</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-4">{t("blog.title")}</h1>
        <p className="max-w-2xl mb-10" style={{ color: "var(--text-secondary)" }}>{t("blog.subtitle")}</p>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center mb-8">
          <form onSubmit={e => { e.preventDefault(); setParam("q", searchInput); }} className="flex gap-2 flex-1 min-w-[260px]">
            <div className="flex-1 flex items-center gap-2 border border-slate-900 px-3 bg-[color:var(--bg-surface)]">
              <Search className="w-4 h-4 opacity-50"/>
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={t("blog.search_ph")} className="w-full py-2 outline-none bg-transparent text-sm" data-testid="blog-search-input"/>
            </div>
            <button type="submit" className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 text-xs font-bold uppercase tracking-wider">OK</button>
          </form>
        </div>
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            <button onClick={() => setParam("category", "")} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border border-slate-900 ${!cat ? "bg-slate-900 text-white" : ""}`} data-testid="cat-all">{t("blog.cat_all")}</button>
            {categories.map(c => (
              <button key={c.category} onClick={() => setParam("category", c.category)} data-testid={`cat-${c.category}`}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border border-slate-900 ${cat === c.category ? "bg-slate-900 text-white" : ""}`}>
                {c.category} <span className="opacity-50">({c.count})</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-24 font-mono text-sm" style={{ color: "var(--text-secondary)" }}>{t("global.loading")}</div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-24 font-mono text-sm" style={{ color: "var(--text-secondary)" }}>{t("blog.empty")}</div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {data.items.map(p => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group bg-[color:var(--bg-surface)] border border-slate-900/15 card-hover flex flex-col">
                  {p.cover_image_url && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={p.cover_image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest mb-3" style={{ color: "var(--text-secondary)" }}>
                      <span className="bg-[color:var(--solar)]/20 border border-slate-900 px-2 py-0.5 text-slate-900">{p.category}</span>
                      <span className="flex items-center gap-1 font-mono normal-case tracking-normal"><Calendar className="w-3 h-3" />{new Date(p.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>
                    <h2 className="font-display font-bold text-xl leading-tight mb-3 flex-1">{p.title}</h2>
                    <p className="text-sm leading-relaxed line-clamp-3" style={{ color: "var(--text-secondary)" }}>{p.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider">Lire <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 py-2 text-xs font-bold uppercase disabled:opacity-30 flex items-center gap-2"><ChevronLeft className="w-3 h-3"/>Préc.</button>
                <span className="font-mono text-sm px-4">Page {page} / {data.total_pages}</span>
                <button disabled={page >= data.total_pages} onClick={() => setParam("page", String(page + 1))} className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 py-2 text-xs font-bold uppercase disabled:opacity-30 flex items-center gap-2">Suiv.<ChevronRight className="w-3 h-3"/></button>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
